import { Flex, Stack, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Button } from '~/domains/design-system/Button';
import { CurrencyConvertor } from '~/components/utils/formatters';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import { IconEntryRevival } from '~/domains/design-system/icons/IconEntryRevival';

type ReviveEntryScreenProps = {
  onRevive: () => void;
  onCancel: () => void;
  onDepositClick: () => void;
  entryName: string;
  balanceInDollars: number;
  contest: FullContest;
  revivalsAvailable: number;
  isCreateEntriesReady: boolean;
  isBalanceLoading: boolean;
};

function ReviveEntryScreen({
  onRevive,
  onCancel,
  onDepositClick,
  entryName,
  balanceInDollars,
  contest,
  revivalsAvailable,
  isCreateEntriesReady,
  isBalanceLoading,
}: ReviveEntryScreenProps) {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();

  // Calculate revival cost based on contest settings
  const revivalCostInDollars = useMemo(() => {
    const buybackSettings = contest.settings?.buybacks;
    if (buybackSettings?.fixedCostInDollars) {
      return buybackSettings.fixedCostInDollars;
    }
    if (buybackSettings?.feeMultiplier) {
      return (contest.entry_fee / 100) * buybackSettings.feeMultiplier;
    }
    return contest.entry_fee / 100;
  }, [contest]);

  const isOverBalance = balanceInDollars < revivalCostInDollars;
  const isEntertainmentOnly = contest.settings?.entertainmentOnlySettings?.enabled;

  return (
    <Stack align="center" spacing={theme.spacing.xl}>
      <Stack align="center" spacing={theme.spacing.sm}>
        <Flex
          sx={{ borderRadius: '100%' }}
          justify="center"
          align="center"
          w={40}
          h={40}
          bg={theme.colors.gray2[0]}
        >
          <IconEntryRevival width={20} height={20} />
        </Flex>

        <Title variant="headline-xl" color={theme.colors.gray2[7]}>
          {t('common.reviveEntryModal.reviveEntry.title', { entryName })}
        </Title>

        <Text variant="body-large" color={theme.colors.gray2[4]}>
          {t('common.reviveEntryModal.reviveEntry.revivalsAvailable', {
            count: revivalsAvailable,
          })}
        </Text>

        {!isEntertainmentOnly && (
          <Text variant="title-medium" color={theme.colors.gray2[3]}>
            {t('common.reviveEntryModal.reviveEntry.balance', {
              balance: CurrencyConvertor(balanceInDollars),
            })}
          </Text>
        )}
      </Stack>

      <Stack spacing={theme.spacing.sm} w="100%">
        {isOverBalance && !isEntertainmentOnly ? (
          <Button
            variant="primary-fill"
            size="large"
            onClick={onDepositClick}
            disabled={!isCreateEntriesReady}
            loading={isBalanceLoading}
          >
            {t('common.reviveEntryModal.reviveEntry.depositToRevive', {
              amount: CurrencyConvertor(revivalCostInDollars - balanceInDollars),
            })}
          </Button>
        ) : (
          <Button
            variant="primary-fill"
            size="large"
            onClick={onRevive}
            disabled={!isCreateEntriesReady}
            loading={isBalanceLoading}
          >
            {isEntertainmentOnly
              ? t('common.reviveEntryModal.reviveEntry.revive')
              : t('common.reviveEntryModal.reviveEntry.reviveFor', {
                  cost: CurrencyConvertor(revivalCostInDollars),
                })}
          </Button>
        )}

        <Button variant="text" size="large" onClick={onCancel}>
          {t('common.reviveEntryModal.reviveEntry.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}

export default ReviveEntryScreen;
